<template>
  <div class="product-card" @click="$emit('open-detail', product)">
    <!-- Bild -->
    <div class="product-image-wrap">
      <img v-if="imageUrl" :src="imageUrl" :alt="product.title" class="product-image" @error="imageError = true" />
      <div v-else class="product-image-placeholder">
        <v-icon size="48" color="grey lighten-1">mdi-image-off</v-icon>
      </div>

      <!-- Cart-Badge -->
      <div v-if="cartQuantity > 0" class="cart-badge">
        <v-icon x-small color="white">mdi-cart</v-icon>
        <span class="ml-1">{{ cartQuantity }}</span>
      </div>

      <!-- Mehrere Bilder Indikator -->
      <div v-if="product.images && product.images.length > 1" class="image-count">
        <v-icon x-small color="white">mdi-image-multiple</v-icon>
        <span class="ml-1">{{ product.images.length }}</span>
      </div>
    </div>

    <!-- Inhalt -->
    <div class="product-body">
      <div class="product-title">{{ product.title }}</div>
      <div v-if="product.description" class="product-description">
        {{ product.description }}
      </div>

      <div class="product-meta">
        <span class="grey--text caption"> {{ product.minQuantity }} – {{ product.maxQuantity }} Stk </span>
      </div>

      <!-- Quantity Stepper + Add-Button -->
      <div class="product-actions" @click.stop>
        <div class="qty-stepper">
          <v-btn color="white" icon x-small @click="decreaseQty" :disabled="qty <= product.minQuantity">
            <v-icon small>mdi-minus</v-icon>
          </v-btn>
          <input v-model.number="qty" type="number" class="qty-input" :min="product.minQuantity" :max="product.maxQuantity" @blur="clampQty" />
          <v-btn color="white" icon x-small @click="increaseQty" :disabled="qty >= product.maxQuantity">
            <v-icon small>mdi-plus</v-icon>
          </v-btn>
        </div>

        <v-btn color="primary" depressed rounded small class="add-btn" @click="addToCart">
          <v-icon left small>mdi-cart-plus</v-icon>
          <span class="d-none d-sm-inline">Hinzufügen</span>
          <span class="d-inline d-sm-none">+</span>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"

export default {
  name: "ProductCard",
  props: {
    product: { type: Object, required: true },
    cartQuantity: { type: Number, default: 0 },
  },
  data() {
    return {
      qty: this.product.minQuantity || 1,
      imageUrl: null,
      imageError: false,
    }
  },
  watch: {
    "product._id"() {
      this.qty = this.product.minQuantity || 1
      this.loadImage()
    },
  },
  methods: {
    async loadImage() {
      if (this.imageUrl) {
        URL.revokeObjectURL(this.imageUrl)
        this.imageUrl = null
      }
      const firstImage = this.product.images && this.product.images[0]
      if (!firstImage) return
      try {
        this.imageUrl = await api.marketingShop.loadProductImage(this.product._id, firstImage.fileId)
      } catch (e) {
        console.error("Bild konnte nicht geladen werden:", e)
        this.imageError = true
      }
    },
    increaseQty() {
      this.qty = Math.min((this.qty || 1) + 1, this.product.maxQuantity || 100)
    },
    decreaseQty() {
      this.qty = Math.max((this.qty || 1) - 1, this.product.minQuantity || 1)
    },
    clampQty() {
      const min = this.product.minQuantity || 1
      const max = this.product.maxQuantity || 100
      let v = parseInt(this.qty) || min
      v = Math.max(min, Math.min(max, v))
      this.qty = v
    },
    addToCart() {
      this.clampQty()
      this.$emit("add-to-cart", { product: this.product, quantity: this.qty })
      // visuelles Feedback: Reset auf min nach Add
      this.qty = this.product.minQuantity || 1
    },
  },
  mounted() {
    this.loadImage()
  },
  beforeDestroy() {
    if (this.imageUrl) URL.revokeObjectURL(this.imageUrl)
  },
}
</script>

<style scoped>
.product-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  flex-direction: column;
}

.product-card:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(0, 150, 64, 0.4);
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

.product-image-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: rgba(255, 255, 255, 0.02);
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image {
  transform: scale(1.05);
}

.product-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.02);
}

.cart-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #009640;
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.image-count {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
  display: flex;
  align-items: center;
}

.product-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.product-title {
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 4px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-description {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 8px;
  flex-grow: 1;
}

.product-meta {
  margin-bottom: 10px;
}

.product-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.qty-stepper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 2px;
}

.qty-input {
  width: 36px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.add-btn {
  flex-grow: 1;
  text-transform: none !important;
  font-weight: 600;
}

@media (max-width: 600px) {
  .product-body {
    padding: 10px;
  }
  .product-title {
    font-size: 13.5px;
  }
  .product-description {
    font-size: 11.5px;
    -webkit-line-clamp: 1;
  }
}
</style>
